/* ==========================================
   ⚠️ CUSTOM ASSET: Native Cart Items Section-List Patch
   Last Modified: 2026-07-29
   Author: Chad Walker (via Claude Code)
   ==========================================

   Purpose:
     Extends Dawn's own CartItems.getSectionsToRender() (assets/cart.js,
     unmodified) and CartDrawerItems.getSectionsToRender() (assets/
     cart-drawer.js, unmodified) so the placeable shipping progress bar
     section (sections/wf-shipping-progress-bar.liquid) rides along on
     Dawn's own native quantity-stepper and line-remove requests, the
     same way it already rides along on this theme's own
     WF.addToCart/removeFromCart requests (assets/wf-upsell.js).

     Retires the 2026-07-29 pub/sub workaround (subscribing to
     PUB_SUB_EVENTS.cartUpdate for Dawn's hardcoded source: 'cart-items'
     and firing an independent follow-up fetch) per Chad's explicit call:
     "if you need to directly edit the file, fork it and point to our
     wf- file instead" — Dawn's own cart.js/cart-drawer.js are still
     completely untouched; this file patches their registered custom
     elements' prototypes from the outside after the fact, so there's
     nothing to keep re-merging against Dawn's own file if it changes
     upstream, only this one method's contract (an array of
     {id, section, selector} objects) to keep matching.

     Chosen over a full fork of cart.js because Dawn's own
     updateQuantity() method already does everything else correctly and
     generically off of getSectionsToRender()'s output — building the
     request's sections list, fetching, and applying each entry's
     response HTML. Extending just that one method means every other
     native behavior (debounce, accessibility live regions, focus
     trapping, error handling) stays exactly Dawn's own code, doing
     exactly what it already did, with zero duplication to maintain.

   Behavior:
     - Waits for each custom element to be defined (works regardless of
       script load order, and regardless of which one, or both, this
       theme's cart_type setting actually results in being registered).
     - Wraps the original getSectionsToRender with a version that calls
       it first, then appends one entry per [data-wf-ship-bar-section]
       element actually in the DOM right now — zero placed instances
       adds nothing; two placed instances (Chad comparing placements)
       each get their own entry and refresh independently, since Dawn's
       own forEach-based application loop is per-entry already.
     - Each bar section wrapper needs a real DOM id for
       document.getElementById(section.id) (Dawn's own lookup mechanism)
       to find it — see sections/wf-shipping-progress-bar.liquid, which
       sets id="{{ section.id }}" alongside the existing
       data-id="{{ section.id }}" attribute this file reads.
     - Does not touch fetch, apply, or error-handling logic at all —
       once the section is in the list Dawn's own unmodified
       updateQuantity() takes care of the rest, identically to how it
       already handles main-cart-items/main-cart-footer.

   Usage:
     Loaded unconditionally in layout/theme.liquid, after cart.js/
     cart-drawer.js (order doesn't strictly matter given the
     whenDefined-based approach, but keeping it near them is clearer).

   Dependencies:
     - assets/cart.js, assets/cart-drawer.js (Dawn originals, read but
       never modified on disk — patched at runtime instead)
     - sections/wf-shipping-progress-bar.liquid (id + data-wf-ship-bar-
       section attributes)
   ==========================================
*/

(function () {
  'use strict';

  function shipBarSectionEntries() {
    var entries = [];
    document.querySelectorAll('[data-wf-ship-bar-section]').forEach(function (el) {
      if (!el.id || !el.dataset.id) return;
      entries.push({ id: el.id, section: el.dataset.id, selector: '.js-contents' });
    });
    return entries;
  }

  function patch(ElementClass) {
    if (!ElementClass || ElementClass.prototype.__wfShipBarPatched) return;

    var original = ElementClass.prototype.getSectionsToRender;
    ElementClass.prototype.getSectionsToRender = function () {
      return original.call(this).concat(shipBarSectionEntries());
    };
    ElementClass.prototype.__wfShipBarPatched = true;
  }

  ['cart-items', 'cart-drawer-items'].forEach(function (tagName) {
    var existing = customElements.get(tagName);
    if (existing) {
      patch(existing);
    } else {
      customElements.whenDefined(tagName).then(function () {
        patch(customElements.get(tagName));
      });
    }
  });
})();
